import { useState } from 'react';
import { Link } from 'react-router';
import { Lightbox } from '@astryxdesign/core/Lightbox';
import { DownloadSimpleIcon, NavigationArrowIcon, PhoneIcon } from '@phosphor-icons/react';
import { Notice, PillLink } from '@/components/kit';
import { Reveal } from '@/components/Reveal';
import { infoIcon } from '@/components/icons';
import { formatLocalDay, formatPhoneHref, formatPrice, hoursRows, plural } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { useDocumentTitle } from '@/lib/hooks';
import { isStandalone } from '@/lib/pwa/platform';
import { useStudio } from '@/features/studio/studio-data';
import { useBookingNav } from '@/features/booking/useBookingNav';
import { ServiceCard } from '@/features/booking/steps/ServiceStep';
import { useAssistantNav } from '@/features/assistant/useAssistantNav';
import { AssistantPromo } from './AssistantPromo';
import { Hero } from './Hero';

export function HomePage() {
  const studio = useStudio();
  const booking = useBookingNav();
  const assistant = useAssistantNav();
  const [lightbox, setLightbox] = useState<number | null>(null);
  useDocumentTitle(`${studio.name} — онлайн-запись`);

  const base = `/s/${studio.slug}`;
  const minPrice = studio.services.length ? Math.min(...studio.services.map((s) => s.priceMinor)) : null;
  const servicesCount = studio.services.length;
  const featured = (
    studio.services.some((s) => s.isFeatured) ? studio.services.filter((s) => s.isFeatured) : studio.services
  ).slice(0, 4);
  const closures = studio.exceptions.filter((e) => e.opensAt === null).slice(0, 2);
  const logo = mediaUrl(studio.logoPath);

  return (
    <div className="ui-page ui-page--home">
      <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
        <Hero studio={studio} onBook={() => booking.open()} />
        <div className="ui-facts" aria-label="Коротко о студии">
          <div className="ui-fact">
            <span className="ui-fact__value">{servicesCount}</span>
            <span className="ui-caption">{plural(servicesCount, 'услуга', 'услуги', 'услуг')}</span>
          </div>
          <div className="ui-fact">
            <span className="ui-fact__value">{studio.resourceCount}</span>
            <span className="ui-caption">{plural(studio.resourceCount, 'бокс', 'бокса', 'боксов')} в работе</span>
          </div>
          {minPrice !== null ? (
            <div className="ui-fact">
              <span className="ui-fact__value ui-accent">{formatPrice(minPrice, studio.currency, 'fixed')}</span>
              <span className="ui-caption">цены от</span>
            </div>
          ) : null}
        </div>
        {studio.isPreview ? (
          <Notice title="Это образец студии">Записи демонстрационные, уведомления не отправляются.</Notice>
        ) : null}
      </div>

      {featured.length ? (
        <Reveal as="section">
          <div className="ui-section">
            <div className="ui-section__head">
              <h2 className="ui-h2">Популярное</h2>
              <Link to={`${base}/services`} className="ui-link-quiet">
                Все {servicesCount} {plural(servicesCount, 'услуга', 'услуги', 'услуг')}
              </Link>
            </div>
            <div className="ui-svc-list">
              {featured.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  currency={studio.currency}
                  onSelect={() => booking.open(service.id)}
                />
              ))}
            </div>
          </div>
        </Reveal>
      ) : null}

      {studio.aiEnabled ? (
        <Reveal>
          <AssistantPromo onOpen={assistant.open} />
        </Reveal>
      ) : null}

      {studio.gallery.length > 0 ? (
        <Reveal as="section">
          <div className="ui-section">
            <h2 className="ui-h2">Наши работы</h2>
            <div className="ui-rail" role="list" aria-label="Фотографии работ">
              {studio.gallery.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="listitem"
                  className="ui-work"
                  onClick={() => setLightbox(index)}
                  aria-label={`Открыть фото: ${item.caption || item.alt || 'работа'}`}
                >
                  <span className="ui-stack" style={{ ['--ui-gap' as string]: '8px' }}>
                    <span className="ui-work__img">
                      <img
                        src={mediaUrl(item.imagePath) ?? ''}
                        alt={item.alt || item.caption}
                        loading="lazy"
                        crossOrigin="anonymous"
                      />
                    </span>
                    {item.caption ? <span className="ui-caption">{item.caption}</span> : null}
                  </span>
                </button>
              ))}
            </div>
            <Lightbox
              isOpen={lightbox !== null}
              onOpenChange={(open) => !open && setLightbox(null)}
              index={lightbox ?? 0}
              onIndexChange={setLightbox}
              media={studio.gallery.map((g) => ({
                src: mediaUrl(g.imagePath) ?? '',
                alt: g.alt || g.caption,
                caption: g.caption,
              }))}
            />
          </div>
        </Reveal>
      ) : null}

      {studio.infoCards.length > 0 ? (
        <Reveal as="section">
          <div className="ui-section" style={{ gap: 'var(--spacing-2)' }}>
            <h2 className="ui-h2" style={{ marginBottom: 'var(--spacing-2)' }}>
              Почему к нам
            </h2>
            <div className="ui-list ui-list--plain ui-list--lined">
              {studio.infoCards.map((card) => {
                const CardIcon = infoIcon(card.icon);
                return (
                  <div key={card.position} className="ui-list__item ui-list__item--static">
                    <CardIcon size={22} className="ui-list__icon" aria-hidden />
                    <span className="ui-stack ui-grow" style={{ ['--ui-gap' as string]: '2px' }}>
                      <span className="ui-title">{card.title}</span>
                      {card.body ? <span className="ui-muted">{card.body}</span> : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      ) : null}

      <Reveal as="section">
        <div className="ui-card ui-contacts">
          <h2 className="ui-h3">Как нас найти</h2>
          <div className="ui-stack" style={{ ['--ui-gap' as string]: '4px' }}>
            <span className="ui-title">{studio.address}</span>
            {studio.directions ? <span className="ui-muted">{studio.directions}</span> : null}
          </div>
          <dl className="ui-hours">
            {hoursRows(studio.hours).map((row) => (
              <div key={row.days} className="ui-hours__row">
                <dt>{row.days}</dt>
                <dd className="ui-num">{row.text}</dd>
              </div>
            ))}
          </dl>
          {closures.length ? (
            <div className="ui-stack" style={{ ['--ui-gap' as string]: '2px' }}>
              {closures.map((c) => (
                <span key={c.day} className="ui-caption">
                  {formatLocalDay(c.day, 'd MMMM')} — выходной{c.note ? ` (${c.note})` : ''}
                </span>
              ))}
            </div>
          ) : null}
          <div className={studio.mapUrl ? 'ui-grid-2' : 'ui-stack'}>
            {studio.mapUrl ? (
              <PillLink href={studio.mapUrl} external block icon={NavigationArrowIcon}>
                Маршрут
              </PillLink>
            ) : null}
            <PillLink href={formatPhoneHref(studio.phone)} block icon={PhoneIcon}>
              Позвонить
            </PillLink>
          </div>
        </div>
      </Reveal>

      {!isStandalone() ? (
        <Reveal as="section">
          <div className="ui-card">
            <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
              <div className="ui-row">
                {logo ? <img className="ui-appicon" src={logo} alt="" crossOrigin="anonymous" /> : null}
                <span className="ui-stack" style={{ ['--ui-gap' as string]: '2px' }}>
                  <span className="ui-title">{studio.shortName}</span>
                  <span className="ui-caption">Приложение студии</span>
                </span>
              </div>
              <h3 className="ui-h2">Запись в одно касание с экрана телефона</h3>
              <p className="ui-muted">Иконка студии рядом с другими приложениями — без поиска ссылки и звонков.</p>
              <PillLink to={`${base}/app`} block icon={DownloadSimpleIcon}>
                Добавить на экран
              </PillLink>
            </div>
          </div>
        </Reveal>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Link to={`${base}/owner/`} className="ui-btn ui-btn--ghost ui-btn--sm">
          Вход для студии
        </Link>
      </div>
    </div>
  );
}
